import React, { useMemo } from "react";
import { Card, Table, Button, Avatar, Progress, Tooltip } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { ClassChips } from "./ClassChips";
import { scoreColor, initials, formatShortDate } from "./evalDashboardUtils";
import styles from "./TeacherEvalDashboard.module.scss";

// T3 — Alumnos de la clase: tabla con examenes realizados, media % y ultimo intento.
export function ClassStudents({
  classNumbers,
  selectedClass,
  onSelectClass,
  summary,
  loadingSummary,
  onOpenStudent,
}) {
  const { t } = useTranslation("evaluations");
  const students = summary?.students || [];
  const examsTotal = summary?.examsTotal ?? 0;

  const columns = useMemo(
    () => [
      {
        title: t("dashboard.colStudent"),
        key: "student",
        render: (_, r) => (
          <div className={styles.studentCell}>
            <Avatar size={32} src={r.avatar || undefined}>
              {initials(r.name, r.surname)}
            </Avatar>
            <span className={styles.studentName}>
              {r.name} {r.surname}
            </span>
          </div>
        ),
      },
      {
        title: t("dashboard.colExamsDone"),
        key: "examsDone",
        width: 220,
        responsive: ["md"],
        render: (_, r) => {
          const total = r.examsTotal || examsTotal || 0;
          const pct = total ? Math.round((r.examsDone / total) * 100) : 0;
          return (
            <div className={styles.progressWrap}>
              <span>
                {t("dashboard.examsXofY", { done: r.examsDone, total })}
              </span>
              <Progress
                percent={pct}
                showInfo={false}
                size="small"
                strokeColor="#00a75a"
                style={{ width: 90, margin: 0 }}
              />
            </div>
          );
        },
      },
      {
        title: t("dashboard.colAverage"),
        key: "avgScore",
        width: 110,
        align: "center",
        sorter: (a, b) => (a.avgScore || 0) - (b.avgScore || 0),
        render: (_, r) =>
          r.examsDone > 0 ? (
            <span
              className={styles.scorePill}
              style={{ background: scoreColor(r.avgScore) }}
            >
              {r.avgScore} %
            </span>
          ) : (
            <span className={styles.doneBadge}>—</span>
          ),
      },
      {
        title: t("dashboard.colLastAttempt"),
        key: "lastAttemptAt",
        width: 140,
        align: "center",
        responsive: ["md"],
        render: (_, r) => formatShortDate(r.lastAttemptAt),
      },
      {
        title: t("dashboard.colActions"),
        key: "actions",
        width: 100,
        align: "center",
        render: (_, r) => (
          <Tooltip title={t("dashboard.viewStudentTip")}>
            <Button
              className={styles.btnGhost}
              size="small"
              icon={<EyeOutlined />}
              onClick={() => onOpenStudent(r)}
            >
              {t("dashboard.view")}
            </Button>
          </Tooltip>
        ),
      },
    ],
    [examsTotal, onOpenStudent, t],
  );

  return (
    <div className={styles.wrap}>
      <ClassChips
        classes={classNumbers}
        selected={selectedClass}
        onSelect={onSelectClass}
      />
      <span className={styles.sectionMeta}>
        {t("dashboard.studentsInClass", {
          count: students.length,
          class: selectedClass,
        })}
      </span>

      <Card className={styles.examCard} variant="outlined">
        <Table
          rowKey={(r) => r.studentId}
          loading={loadingSummary}
          dataSource={students}
          columns={columns}
          tableLayout="fixed"
          pagination={{ pageSize: 10, showSizeChanger: true }}
        />
      </Card>
    </div>
  );
}
